import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import RegionBar from "@/components/RegionBar";
import { toast } from "sonner";
import { ShoppingBag, ArrowLeft, Truck, RotateCcw, Ruler, MapPin } from "lucide-react";

export default function ProductPage() {
  const { slug } = useParams();
  const { region, fmt, price, priceLabel, img, gallery, addToCart } = useShop();
  const [p, setP] = useState(null);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get(`/shop/products/${slug}`).then(({ data }) => {
      setP(data); setColor(data.colors?.[0] || ""); setSize(data.sizes?.[0] || ""); setActive(0);
    }).catch(() => {});
  }, [slug]);

  if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-[#7a8a80]">Loading…</div>;

  const imgs = gallery(p);
  const mainImg = imgs[active] || img(p);
  const add = () => {
    addToCart({ product_id: p.id, slug: p.slug, name: p.name, image: img(p) || "", size, color, qty, price_intl: p.price_intl, price_eth: p.price_eth });
    toast.success("Added to cart");
  };

  return (
    <div data-testid="product-page" className="bg-[#FAF7F0] text-[#14241C]">
      <RegionBar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#7a8a80] hover:text-[#14241C] mb-6"><ArrowLeft size={15} /> Back to shop</Link>
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="aspect-square rounded-3xl overflow-hidden bg-[#F1EFE8] flex items-center justify-center">
              {mainImg ? (
                <img src={imgUrl(mainImg)} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-[#a9b3ab]" data-testid="product-noimg">
                  <ShoppingBag size={48} />
                  <span className="text-xs uppercase tracking-wider font-semibold">Product mockup coming soon</span>
                </div>
              )}
            </div>
            {imgs.length > 1 && (
              <div className="flex gap-3 mt-4">
                {imgs.map((im, i) => (
                  <button key={im} onClick={() => setActive(i)} data-testid={`product-thumb-${i}`} className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${active === i ? "border-[#14241C]" : "border-transparent"}`}>
                    <img src={imgUrl(im)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            {p.badges?.[0] && <span className="bg-[#E0A526] text-[#14241C] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{p.badges[0]}</span>}
            <h1 className="font-display text-4xl font-bold tracking-tight mt-3">{p.name}</h1>
            <p className="font-display text-2xl font-bold mt-2">{priceLabel(p)} {price(p) > 0 && <span className="text-sm font-normal text-[#7a8a80]">{region.currency}</span>}</p>
            <p className="text-[#4a5a50] mt-4 leading-relaxed">{p.description}</p>

            {p.colors?.length > 0 && (
              <div className="mt-6"><p className="text-sm font-semibold mb-2">Color: <span className="font-normal">{color}</span></p>
                <div className="flex gap-2">{p.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`} className={`px-3 py-1.5 rounded-full border text-sm ${color === c ? "border-[#14241C] bg-[#14241C] text-white" : "border-[#14241C]/20"}`}>{c}</button>
                ))}</div>
              </div>
            )}
            {p.sizes?.length > 0 && (
              <div className="mt-5"><p className="text-sm font-semibold mb-2">Size</p>
                <div className="flex flex-wrap gap-2">{p.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`} className={`min-w-[3rem] px-3 py-2 rounded-lg border text-sm ${size === s ? "border-[#14241C] bg-[#14241C] text-white" : "border-[#14241C]/20"}`}>{s}</button>
                ))}</div>
              </div>
            )}
            <div className="mt-5 flex items-center gap-3">
              <p className="text-sm font-semibold">Qty</p>
              <div className="flex items-center border border-[#14241C]/20 rounded-lg">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2">−</button>
                <span className="px-3 min-w-[2rem] text-center" data-testid="qty">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-3 py-2">+</button>
              </div>
            </div>

            <button onClick={add} data-testid="add-to-cart" className="mt-7 w-full bg-[#14241C] text-white rounded-full py-4 font-bold hover:bg-[#0e1a13] transition-colors inline-flex items-center justify-center gap-2"><ShoppingBag size={18} /> Add to Cart</button>
            <p className="text-sm text-[#7a8a80] mt-3 text-center">Your purchase helps support Refugium's mission and our goal of creating meaningful opportunities in Ethiopia.</p>

            <div className="mt-8 space-y-3 text-sm border-t border-[#14241C]/10 pt-6">
              {p.material && <p className="flex gap-2"><Ruler size={16} className="text-[#B45B3E] mt-0.5 shrink-0" /><span><strong>Material:</strong> {p.material}</span></p>}
              {p.care && <p className="flex gap-2"><RotateCcw size={16} className="text-[#B45B3E] mt-0.5 shrink-0" /><span><strong>Care:</strong> {p.care}</span></p>}
              {p.production_location && <p className="flex gap-2"><MapPin size={16} className="text-[#B45B3E] mt-0.5 shrink-0" /><span><strong>Production:</strong> {p.production_location}</span></p>}
              <p className="flex gap-2"><Truck size={16} className="text-[#B45B3E] mt-0.5 shrink-0" /><span><strong>Shipping:</strong> {region.market === "ethiopia" ? "Local delivery within Ethiopia (where available)." : "Worldwide shipping to supported destinations."} Returns accepted on unworn items within 30 days.</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* sticky mobile add-to-cart */}
      <div className="lg:hidden sticky bottom-0 z-30 bg-white border-t border-[#14241C]/10 p-3 flex items-center gap-3">
        <span className="font-display text-lg font-bold">{priceLabel(p)}</span>
        <button onClick={add} data-testid="add-to-cart-sticky" className="flex-1 bg-[#14241C] text-white rounded-full py-3 font-bold">Add to Cart</button>
      </div>
    </div>
  );
}
